import Big from 'big.js';
import { endOfDay } from 'date-fns';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FormikErrors, FormikProvider, useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import { BigRoundMode } from '@/modules/common/constants/big-round-mode.ts';
import { SelectPortfolio } from '@/modules/issuer-dashboard/components/portfolios/SelectPortfolio.tsx';
import { useIssuerAddNewNavPortfolioMutation } from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import { useRegisterClassesUnitsQuery } from '@/modules/issuer-dashboard/queries/securities.ts';
import {
  Alert,
  BaseModalProps,
  Button,
  CalendarInput,
  CalendarInputProps,
  ModalContentWrapper,
  Select,
  SelectPaginateOption,
  SelectWithCreatableOption,
  Spinner,
} from 'ui';
import { AlertBlueIcon, PlusBlueIcon, TrashIcon } from 'ui/icons';
import { CreatePortfolioNavsRequest, PortfolioModel, RegisterClass } from 'api';

dayjs.extend(utc);

export interface IssuerAddNewNavModalProps extends BaseModalProps {
  portfolio?: PortfolioModel;
  fromPortfolio?: boolean;
}

export const IssuerAddNewNavModal = ({
  open,
  onClose,
  portfolio,
  fromPortfolio = false,
}: IssuerAddNewNavModalProps) => {
  return (
    <ModalContentWrapper onClose={onClose} open={open} title={'Add new NAV'}>
      <AddNewNavBody onSuccess={onClose} fromPortfolio={fromPortfolio} portfolio={portfolio} />
    </ModalContentWrapper>
  );
};

interface ClassInfoModel {
  id: number;
  units_on_issue: number;
}

interface CreatePortfolioNavsRequestEx extends CreatePortfolioNavsRequest {
  classes_info: ClassInfoModel[];
  portfolio_value: number;
  total_liabilities: number;
  portfolio_id: number;
}

const roundMoneyValue = (value?: number | string | null) =>
  Number(new Big(value || 0).round(2, BigRoundMode.roundHalfUp).valueOf());

const validationSchema = yup.object<CreatePortfolioNavsRequestEx>({
  date: yup.string().trim().required('This field is required'),
  classes: yup.array().of(yup.number()).nullable().optional(),
  classes_info: yup.array().of(
    yup.object<ClassInfoModel>({
      id: yup.number(),
      units_on_issue: yup
        .number()
        .typeError('Must be a number')
        // .moreThan(0, 'Must be more than 0')
        .required('This field is required'),
    }),
  ),
  total_liabilities: yup
    .number()
    .typeError('Price must be a number')
    .required('This field is required'),
  portfolio_value: yup
    .number()
    .typeError('Price must be a number')
    .moreThan(0, 'Price must be more than 0')
    .required('This field is required'),
  nav: yup
    .number()
    .typeError('Price must be a number')
    .moreThan(0.009999999999, 'Price must be greater than or equal to 0.01')
    .required('This field is required'),
  portfolio_id: yup.number().required('This field is required'),
});

export interface AddNewNavBodyProps {
  onSuccess?: () => void;
  fromPortfolio?: boolean;
  portfolio?: PortfolioModel;
}

export const AddNewNavBody = ({ onSuccess, fromPortfolio, portfolio }: AddNewNavBodyProps) => {
  const [currentPortfolio, setCurrentPortfolio] = useState<PortfolioModel | undefined>(portfolio);
  const getInitialValues = useCallback(
    (item?: PortfolioModel, date = ''): CreatePortfolioNavsRequestEx => {
      const init = {
        nav: 0,
        classes: !date ? [] : item?.portfolio_classes?.map<number>(item => item.id) || [],
        classes_info: !date
          ? []
          : item?.portfolio_classes?.map<ClassInfoModel>(item => ({
              id: item.id,
              units_on_issue: item.issued_shares || 0,
            })) || [],
        date,
        portfolio_value: !date ? 0 : roundMoneyValue(item?.portfolio_value),
        portfolio_id: item?.id,
        total_liabilities: 0,
        is_from_portfolio_edited: false,
      } as CreatePortfolioNavsRequestEx;

      const units_on_issues = init.classes_info.reduce((prev, curr) => {
        prev = prev + curr.units_on_issue;
        return prev;
      }, 0);

      const nav = Number(
        new Big(init.portfolio_value)
          .div(units_on_issues || 1)
          .round(4, BigRoundMode.roundHalfUp)
          .valueOf(),
      );
      return { ...init, nav };
    },
    [],
  );

  const formik = useFormik<CreatePortfolioNavsRequestEx>({
    validationSchema,
    initialValues: getInitialValues(
      currentPortfolio,
      dayjs().utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]'),
    ),
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: ({ nav, classes, date, is_from_portfolio_edited }) => {
      mutateAsyncAdd({
        nav,
        classes,
        date,
        is_from_portfolio_edited,
      });
    },
  });

  const { data: dataClassesUnits, isFetching: isFetchingClassesUnits } =
    useRegisterClassesUnitsQuery(
      {
        //register_class_ids: currentPortfolio?.portfolio_classes?.map(item => item.id).join(', '),
        date_to: formik.values.date ? endOfDay(new Date(formik.values.date)).toISOString() : '',
        // date_from: formik.values.date ? startOfDay(new Date(formik.values.date)).toISOString() : '',
      },
      !currentPortfolio?.id || isNaN(+currentPortfolio?.id) || !formik.values.date,
    );

  useEffect(() => {
    if (dataClassesUnits?.data && currentPortfolio) {
      formik.setValues(
        getInitialValues(
          {
            ...currentPortfolio,
            portfolio_classes: dataClassesUnits?.data?.map<RegisterClass>(item => ({
              id: item.id,
              class_name: item.name,
              issued_shares: item.total_units,
              current_price_per_share: 0,
              min_investment: 0,
              register_id: currentPortfolio?.register_id,
            })),
          },
          formik.values.date,
        ),
      );
    }
  }, [dataClassesUnits?.data, currentPortfolio, formik.values.date]);

  const { mutateAsync: mutateAsyncAdd, isLoading: isLoadingAdd } =
    useIssuerAddNewNavPortfolioMutation({
      setErrors: formik.setErrors,
      onSuccess,
    });

  const handleChangeNAV = useCallback((nav: number | string) => {
    formik.setFieldValue('nav', nav || 0);
    formik.setFieldError('nav', '');

    // const units_on_issues = formik.values.classes_info
    //   .filter(item => !!item.id)
    //   .reduce((prev, curr) => {
    //     prev = prev + curr.units_on_issue || 0;
    //     return prev;
    //   }, 0);
    //
    // const total_liabilities = formik.values.total_liabilities;
    //
    // const portfolio_value = Number(
    //   new Big(nav || 0)
    //     .times(units_on_issues || 1)
    //     .plus(total_liabilities || 0)
    //     .round(6, 1)
    //     .valueOf(),
    // );

    formik.setFieldValue('is_from_portfolio_edited', true);
    formik.setFieldError('is_from_portfolio_edited', '');
  }, []);

  const handleChangePortfolioValue = useCallback(
    (value: number | string) => {
      formik.setFieldValue('portfolio_value', value);
      formik.setFieldError('portfolio_value', '');

      const classes = formik.values.classes_info;
      const units_on_issues = classes
        .filter(item => !!item.id)
        .reduce((prev, curr) => {
          prev = prev + curr.units_on_issue || 0;
          return prev;
        }, 0);

      const nav = Number(
        new Big(value || 1)
          .minus(formik.values.total_liabilities || 0)
          .div(units_on_issues || 1)
          .round(4, BigRoundMode.roundHalfUp)
          .valueOf(),
      );
      formik.setFieldValue('nav', nav);
      formik.setFieldError('nav', '');
    },
    [formik.values.total_liabilities, formik.values.classes_info],
  );

  const handleChangeTotalLiabilities = useCallback(
    (total_liabilities: number | string) => {
      formik.setFieldValue('total_liabilities', total_liabilities);
      formik.setFieldError('total_liabilities', '');

      const classes = formik.values.classes_info;
      const units_on_issues = classes
        .filter(item => !!item.id)
        .reduce((prev, curr) => {
          prev = prev + curr.units_on_issue || 0;
          return prev;
        }, 0);

      const nav = Number(
        new Big(formik.values.portfolio_value || 1)
          .minus(total_liabilities || 0)
          .div(units_on_issues || 1)
          .round(4, BigRoundMode.roundHalfUp)
          .valueOf(),
      );
      formik.setFieldValue('nav', nav);
      formik.setFieldError('nav', '');
    },
    [formik.values.portfolio_value, formik.values.classes_info],
  );

  const handleChangeDate =
    (fieldName: keyof CreatePortfolioNavsRequestEx): CalendarInputProps['onChange'] =>
    date => {
      if (!date) {
        formik.setFieldValue(fieldName, '');
        formik.setFieldError(fieldName, '');
        return;
      }

      const currentDate = date.utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]');
      formik.setFieldValue(fieldName, currentDate);
      formik.setFieldError(fieldName, '');
    };

  const optionsClasses = useMemo<SelectWithCreatableOption<ClassInfoModel, string>[]>(() => {
    if (!dataClassesUnits?.data) return [];

    return (
      dataClassesUnits?.data?.map(item => ({
        label: item.name,
        value: {
          units_on_issue: item.total_units || 0,
          id: item.id,
        },
      })) || []
    );
  }, [dataClassesUnits?.data]);

  const handleChangeSelectPortfolio = useCallback(
    (item: SelectPaginateOption<PortfolioModel> | null) => {
      setCurrentPortfolio(item?.value);
      formik.setFieldValue('portfolio_id', item?.value?.id);
      formik.setFieldError('portfolio_id', '');
      formik.setFieldError('portfolio_value', '');
      formik.setFieldError('nav', '');
    },
    [],
  );

  const handleChangeClass = useCallback(
    (index: number) => (value: number | null) => {
      const selected = optionsClasses.find(item => item.value.id === value);
      const newClasses = formik.values.classes_info.map((item, i) => {
        if (i === index) {
          return selected?.value || { id: 0, units_on_issue: 0 };
        }
        return item;
      });
      formik.setFieldValue('classes_info', newClasses);
      formik.setFieldError('classes_info', '');
      formik.setFieldValue(
        'classes',
        newClasses.map(item => item.id),
      );
      formik.setFieldError('classes', '');

      const units_on_issues = newClasses
        .filter(item => !!item.id)
        .reduce((prev, curr) => {
          prev = prev + curr.units_on_issue || 0;
          return prev;
        }, 0);

      const nav = Number(
        new Big(formik.values.portfolio_value || 1)
          .minus(formik.values.total_liabilities || 0)
          .div(units_on_issues || 1)
          .round(4, BigRoundMode.roundHalfUp)
          .valueOf(),
      );
      formik.setFieldValue('nav', nav);
      formik.setFieldError('nav', '');
    },
    [
      formik.values.classes_info,
      formik.values.portfolio_value,
      formik.values.total_liabilities,
      optionsClasses,
    ],
  );

  const handleChangeClassField = useCallback(
    (index: number, fieldName: string) => (value: any) => {
      const newClasses = formik.values.classes_info.map((item, i) => {
        if (i === index) {
          return {
            ...item,
            [fieldName]: value || 0,
          };
        }
        return item;
      });
      formik.setFieldValue('classes_info', newClasses);
      formik.setFieldError('classes_info', '');
      formik.setFieldValue(
        'classes',
        newClasses.map(item => item.id),
      );
      formik.setFieldError('classes', '');

      const units_on_issues = newClasses
        .filter(item => !!item.id)
        .reduce((prev, curr) => {
          prev = prev + curr.units_on_issue || 0;
          return prev;
        }, 0);
      const nav = Number(
        new Big(formik.values.portfolio_value || 1)
          .minus(formik.values.total_liabilities || 0)
          .div(units_on_issues || 1)
          .round(4, BigRoundMode.roundHalfUp)
          .valueOf(),
      );
      formik.setFieldValue('nav', nav);
      formik.setFieldError('nav', '');
    },
    [formik.values.classes_info, formik.values.portfolio_value, formik.values.total_liabilities],
  );

  const handleAddClass = useCallback(() => {
    const newClasses: ClassInfoModel[] = [
      ...(formik.values.classes_info || []),
      {
        id: 0,
        units_on_issue: 0,
      } as ClassInfoModel,
    ];
    formik.setFieldValue('classes_info', newClasses);
    formik.setFieldError('classes_info', '');
    formik.setFieldValue(
      'classes',
      newClasses.map(item => item.id),
    );
    formik.setFieldError('classes', '');
  }, [formik.values.classes_info]);

  const handleDeleteClass = useCallback(
    (index: number) => () => {
      const newClasses = formik.values.classes_info.filter((_, i) => i !== index);
      formik.setFieldValue('classes_info', newClasses);
      formik.setFieldError('classes_info', '');
      formik.setFieldValue(
        'classes',
        newClasses.map(item => item.id),
      );
      formik.setFieldError('classes', '');

      const units_on_issues = newClasses
        .filter(item => !!item.id)
        .reduce((prev, curr) => {
          prev = prev + curr.units_on_issue || 0;
          return prev;
        }, 0);

      const nav = Number(
        new Big(formik.values.portfolio_value || 1)
          .minus(formik.values.total_liabilities || 0)
          .div(units_on_issues || 1)
          .round(4, BigRoundMode.roundHalfUp)
          .valueOf(),
      );
      formik.setFieldValue('nav', nav);
      formik.setFieldError('nav', '');
    },
    [formik.values.classes_info, formik.values.portfolio_value, formik.values.total_liabilities],
  );

  return (
    <FormikProvider value={formik}>
      <div className="flex flex-col w-full gap-6">
        <CalendarInput
          label="Date"
          name="date"
          onChange={handleChangeDate('date')}
          showTime={true}
          format="DD-MM-YYYY HH:mm"
          error={formik.errors.date}
          value={formik.values.date ? dayjs(formik.values.date) : null}
        />

        {!fromPortfolio && (
          <SelectPortfolio
            value={formik.values.portfolio_id}
            onChange={handleChangeSelectPortfolio}
            error={formik.errors.portfolio_id}
            label="Select Portfolio"
            clearable={true}
          />
        )}

        {isFetchingClassesUnits ? (
          <div className="w-full flex justify-center">
            <Spinner size="lg" />
          </div>
        ) : (
          !!dataClassesUnits?.data && (
            <>
              <CurrencyInput
                name="portfolio_value"
                label="Portfolio Value"
                value={new Big(formik.values.portfolio_value || 0).toFixed(2)}
                error={formik.errors.portfolio_value}
                onChange={handleChangePortfolioValue}
                decimalPoints={6}
                disabled={true}
              />

              <CurrencyInput
                name="total_liabilities"
                label="Total liabilities"
                value={formik.values.total_liabilities}
                error={formik.errors.total_liabilities}
                onChange={handleChangeTotalLiabilities}
                decimalPoints={6}
              />

              <div className="w-full flex gap-4 justify-between items-center">
                <span className="text-[16px] leading-[22px] font-[500] font-[Inter]">Classes</span>
                {optionsClasses.length > formik.values.classes_info.length && (
                  <Button variant="plain" className="flex w-auto gap-1" onClick={handleAddClass}>
                    <img src={PlusBlueIcon} alt="Loading" />
                    <span className="text-[14px] leading-[22px]">Add new class</span>
                  </Button>
                )}
              </div>

              {formik.values.classes_info.map((item, i, allClasses) => {
                const error = formik.errors.classes_info?.[i] as FormikErrors<ClassInfoModel>;
                const idsSelectedClasses = allClasses
                  .map(select => select.id)
                  .filter((_, j) => j !== i);

                return (
                  <div key={i} className="w-full flex flex-wrap sm:flex-nowrap items-start gap-6">
                    <Select
                      value={item.id}
                      placeholder="Select class"
                      label="Class"
                      onChange={handleChangeClass(i)}
                      options={optionsClasses
                        .map(item => ({
                          ...item,
                          value: item.value.id,
                        }))
                        .filter(item => !idsSelectedClasses.includes(item.value))}
                      error={String(error?.id || '') as string}
                      isClearable={true}
                    />

                    <CurrencyInput
                      name="units_on_issue"
                      label="Units on issue"
                      value={item?.units_on_issue || 0}
                      error={String(error?.units_on_issue || '') as string}
                      onChange={handleChangeClassField(i, 'units_on_issue')}
                      decimalPoints={6}
                      disabled={!item?.id || true}
                      currency=""
                    />

                    <span
                      onClick={handleDeleteClass(i)}
                      className="cursor-pointer hover:opacity-70"
                    >
                      <img src={TrashIcon} alt="delete" className="w-6 h-6 min-h-6 min-w-6" />
                    </span>
                  </div>
                );
              })}

              <div className="flex flex-col gap-2">
                <CurrencyInput
                  name="nav"
                  label={`Final NAV`}
                  value={formik.values.nav}
                  error={formik.errors.nav}
                  onChange={handleChangeNAV}
                  decimalPoints={4}
                />
                <span className="text-xs leading-[18px] text-gray-500">
                  NAV = (Portfolio Value - Total Liabilities) / Units On Issue
                </span>
              </div>

              {formik.values.is_from_portfolio_edited && (
                <Alert icon={AlertBlueIcon} variant="neutral-2" className="mb-0">
                  <span className="w-full text-left text-blue-700 whitespace-normal">
                    You changed this final nav manually yourself and we will mark it as edited
                  </span>
                </Alert>
              )}
            </>
          )
        )}
      </div>
      <div className="mt-4 py-3 !pb-0 sm:py-6">
        <Button type="submit" onClick={formik.handleSubmit} loading={isLoadingAdd}>
          Submit
        </Button>
      </div>
    </FormikProvider>
  );
};
